import React, { useState, useEffect, Fragment } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Divider,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 同工列表项 - 企业化设计
const WorkerItem = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.25, 0),
  cursor: "pointer",
  transition: "background-color 0.15s ease",
  borderRadius: "8px",
  px: 1.5,
  mx: -1.5,
  "&:active": {
    backgroundColor: theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.05)"
      : "rgba(0, 0, 0, 0.03)",
  },
}));

const MobileServe = () => {
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const navigate = useNavigate();
  const [ministryWorkers, setMinistryWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failedImages, setFailedImages] = useState(new Set());

  useEffect(() => {
    const fetchMinistryWorkers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/ministry-worker`);
        setMinistryWorkers(response.data);
      } catch (err) {
        console.error("获取同工数据失败:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMinistryWorkers();
  }, []);

  const getImageUrl = (path) => {
    if (!path) return `${API_BASE_URL}/images/avatars/default-avatar.png`;
    return `${API_BASE_URL}${path}`;
  };

  const handleImageError = (workerId) => {
    if (!failedImages.has(workerId)) {
      setFailedImages((prev) => new Set([...prev, workerId]));
    }
  };

  const handleWorkerClick = (worker) => {
    navigate(`/serve/${worker._id}`);
  };

  const textColor = prefersDarkMode ? "#ffffff" : "#000000";
  const secondaryTextColor = prefersDarkMode ? "rgba(235, 235, 245, 0.6)" : "rgba(60, 60, 67, 0.6)";
  const borderColor = prefersDarkMode ? "rgba(84, 84, 88, 0.65)" : "rgba(0, 0, 0, 0.1)";

  return (
    <Box sx={{ px: 2, py: 2.5 }}>
      {/* 页面标题 */}
      <Typography
        variant="h4"
        sx={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: textColor,
          mb: 1,
          lineHeight: 1.2,
          letterSpacing: "-0.02em",
        }}
      >
        事奉同工
      </Typography>
      <Typography
        sx={{
          fontSize: "0.85rem",
          color: secondaryTextColor,
          mb: 3,
          lineHeight: 1.5,
        }}
      >
        了解我们的同工信息，共同参与事奉
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} sx={{ color: textColor }} />
        </Box>
      ) : ministryWorkers.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography sx={{ color: secondaryTextColor, fontSize: "0.9rem" }}>
            暂无同工数据
          </Typography>
        </Box>
      ) : (
        <Box>
          {ministryWorkers.map((worker, index) => (
            <Fragment key={worker._id}>
              <WorkerItem onClick={() => handleWorkerClick(worker)}>
                <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
                  <Avatar
                    src={
                      failedImages.has(worker._id)
                        ? `${API_BASE_URL}/images/avatars/default-avatar.png`
                        : getImageUrl(worker.avatar)
                    }
                    alt={worker.name}
                    onError={() => handleImageError(worker._id)}
                    sx={{
                      width: 56,
                      height: 56,
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: textColor,
                        mb: 0.25,
                        lineHeight: 1.3,
                      }}
                    >
                      {worker.name}
                    </Typography>
                    {worker.position && (
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: secondaryTextColor,
                          mb: 0.5,
                        }}
                      >
                        {worker.position}
                      </Typography>
                    )}
                    {worker.bio && (
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: secondaryTextColor,
                          lineHeight: 1.4,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {worker.bio}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </WorkerItem>
              {index < ministryWorkers.length - 1 && (
                <Divider
                  sx={{
                    borderColor: borderColor,
                    my: 0,
                  }}
                />
              )}
            </Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MobileServe;
